import { Icon as BlueprintIcon } from '@blueprintjs/core';
import omit from 'lodash/omit';
import * as React from 'react';
import { Accordion, Body, Button } from 'vet-bones/design-system/components';
import { isEqual } from 'vet-bones/utils';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import { PanelWithSidebar } from 'src/components/resourceDialog/PanelWithSidebar';
import { SidebarFooterButtons } from 'src/components/resourceDialog/SidebarFooterButtons';
import { getDataSourceTableKey } from 'src/constants/dataSourceTable';
import { TypeDescriptionCombinedOption } from 'src/constants/dataSourceTypeDescription';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ResourceNode } from 'src/containers/ProjectDiagram/types/diagram';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { getMappingsForResource } from 'src/containers/ProjectDiagram/utils/resource/getMappingsForResource';
import { updateMappingsForResourceFields } from 'src/containers/ProjectDiagram/utils/resource/updateMappingsForResourceFields';
import { updateResourceDataSourceType } from 'src/containers/ProjectDiagram/utils/resource/updateResourceDataSourceType';
import { updateResourceName } from 'src/containers/ProjectDiagram/utils/resource/updateResourceName';
import {
  AdvancedOption,
  AdvancedOptions,
} from 'src/containers/ProjectVirtualGraphPreviewDialog/components/AdvancedOptions';
import { VirtualGraphOptionsForm } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphOptionsForm';
import { VirtualGraphPreview } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphPreview';
import { VirtualGraphTableSelection } from 'src/containers/ProjectVirtualGraphPreviewDialog/components/VirtualGraphTableSelection';
import { useProjectVirtualGraphPreviewDialog } from 'src/containers/ProjectVirtualGraphPreviewDialog/context/useProjectVirtualGraphPreviewDialog';
import { useIsVgDirty } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useIsVgDirty';
import { useQueryModeSwitch } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useQueryModeSwitch';
import { useRefreshDataSourceTableMetadata } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useRefreshDataSourceTableMetadata';
import { useRefreshDataSourceTypeMetadata } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useRefreshDataSourceTypeMetadata';
import { useRefreshVgTables } from 'src/containers/ProjectVirtualGraphPreviewDialog/hooks/useRefreshVgTables';
import { DataSourceTypeSelectionPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/DataSourceTypeSelectionPanel';
import { connectionSelector } from 'src/selectors/connection';
import { currentFeatureVersionSelector } from 'src/selectors/currentFeatureVersion';
import { dataSourceNativeQuoteCharacterSelector } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { dataSourceSelector } from 'src/selectors/dataSourceSelector';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { virtualGraphPropertiesSelector } from 'src/selectors/virtualGraphProperties';
import { VirtualGraphOption } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { getDataSourceIcon } from 'src/templates/icons';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { useFlushedDebouncedCallback } from 'src/utils/hooks/useFlushedDebouncedCallback';
import { useProjectDataSourceQuery } from 'src/utils/hooks/useProjectDataSourceQuery';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/components/resourceDialog/styles/resourceDisplayPanel.module.scss';

const STANDARD_OPTIONS = new Set(Object.values(VirtualGraphOption));

export const VirtualGraphOverviewPanel: React.VFC<PanelProps> = ({
  openPanel,
}) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const { resourceId } = useReduxSelector(
    (state) => state.ui.virtualGraphPreviewDialog
  );

  const {
    setFullSize,
    setDialogTitle,
    localVirtualGraph,
    setLocalVirtualGraph,
    localVirtualGraphOptions,
    setLocalVirtualGraphOptions,
    errorText,
    setErrorText,
  } = useProjectVirtualGraphPreviewDialog();

  const [isPropertiesOpen, setIsPropertiesOpen] = React.useState(true);

  const virtualGraph = useReduxSelector(
    projectVirtualGraphSelector(projectId, resourceId)
  );

  const {
    submittedCustomQuery,
    setSubmittedCustomQuery,
    isTableTreeOpen,
    setIsTableTreeOpen,
    expandAllTables,
    handleSetIsUsingCustomQuery,
    initializeFromVirtualGraph,
  } = useQueryModeSwitch({
    localVirtualGraph,
    virtualGraph,
    setLocalVirtualGraph,
  });

  const extractAdvancedOptions = React.useCallback(
    (options: ProjectVirtualGraphOptions | null) => {
      if (!options) {
        return [];
      }
      return Object.entries(options)
        .filter(([key]) => !STANDARD_OPTIONS.has(key as VirtualGraphOption))
        .map(([property, value]) => ({
          property,
          value: value != null ? String(value) : '',
        }));
    },
    []
  );

  const [advancedOptions, setAdvancedOptions] = React.useState<
    AdvancedOption[]
  >(() => {
    const extracted = extractAdvancedOptions(localVirtualGraphOptions);
    return extracted.length > 0 ? extracted : [{ property: '', value: '' }];
  });

  const [isAdvancedOpen, setIsAdvancedOpen] = React.useState(() => {
    const extracted = extractAdvancedOptions(localVirtualGraphOptions);
    return extracted.length > 0;
  });

  const connection = useReduxSelector(connectionSelector);
  const stardogVersion = useReduxSelector(currentFeatureVersionSelector);

  const propertyDescriptions = useReduxSelector(
    virtualGraphPropertiesSelector(
      connection,
      localVirtualGraph?.dataSourceType || virtualGraph?.dataSourceType || null
    ),
    isEqual
  );

  const propertySuggestions = React.useMemo(() => {
    return Object.entries(propertyDescriptions)
      .filter(
        ([property]) => !STANDARD_OPTIONS.has(property as VirtualGraphOption)
      )
      .map(([property]) => property)
      .sort();
  }, [propertyDescriptions]);

  const mergeAdvancedOptionsToParent = React.useCallback(
    (newOptions: AdvancedOption[]) => {
      setLocalVirtualGraphOptions((prev) => {
        if (!prev) {
          return prev;
        }

        const existingStandardOptions = Object.values(
          VirtualGraphOption
        ).reduce(
          (agg, standardOption) => ({
            ...agg,
            [standardOption]: prev[standardOption],
          }),
          {}
        ) as ProjectVirtualGraphOptions;

        const mergedOptions: ProjectVirtualGraphOptions = {
          ...existingStandardOptions,
          ...newOptions
            .filter(({ property }) => property.trim() !== '')
            .reduce(
              (agg, { property, value }) => ({
                ...agg,
                [property]: value || undefined,
              }),
              {}
            ),
        };

        return mergedOptions;
      });
    },
    [setLocalVirtualGraphOptions]
  );

  const debouncedMergeAdvancedOptions = useFlushedDebouncedCallback(
    (newOptions: AdvancedOption[]) => {
      mergeAdvancedOptionsToParent(newOptions);
    },
    500
  );

  const updateAdvancedOptions = React.useCallback(
    (newOptions: AdvancedOption[]) => {
      setAdvancedOptions(newOptions);
      debouncedMergeAdvancedOptions(newOptions);
    },
    [debouncedMergeAdvancedOptions]
  );

  const advancedPropertyMetadata = React.useMemo(() => {
    const result: Record<string, TypeDescriptionCombinedOption> = {};
    Object.entries(propertyDescriptions).forEach(([property, metadata]) => {
      if (
        !STANDARD_OPTIONS.has(property as VirtualGraphOption) &&
        metadata !== undefined
      ) {
        result[property] = metadata;
      }
    });
    return result;
  }, [propertyDescriptions]);

  const dataSourceIdToUse =
    localVirtualGraph?.dataSourceId || virtualGraph?.dataSourceId;
  const dataSource = useReduxSelector(dataSourceSelector(dataSourceIdToUse));

  const dataSourceType =
    localVirtualGraph?.dataSourceType || virtualGraph?.dataSourceType || null;

  const dataSourceIcon = React.useMemo(() => {
    if (!dataSourceType) {
      return null;
    }
    return getDataSourceIcon({
      dataSourceType,
      stardogVersion,
    });
  }, [dataSourceType, stardogVersion]);

  const nativeQuoteCharacter = useReduxSelector(
    dataSourceNativeQuoteCharacterSelector(
      localVirtualGraph?.dataSourceType || virtualGraph?.dataSourceType || null,
      connection
    )
  );

  const isDataSourceQueryOptionsSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isDataSourceQueryOptionsSupported',
      connection
    )
  );

  const tables = React.useMemo(
    () => [...(dataSource?.tables || [])],
    [dataSource?.tables]
  );

  const [forceRefresh, setForceRefresh] = React.useState(false);

  const {
    isRefreshingMetadata,
    isMetadataRefreshable,
    refreshMetadata: refreshDataSourceTypeMetadata,
  } = useRefreshDataSourceTypeMetadata(dataSourceIdToUse, () =>
    setForceRefresh(true)
  );

  const tableName =
    typeof localVirtualGraph?.from === 'object'
      ? localVirtualGraph.from.name
      : null;

  const { isRefreshing, refreshPreview } = useRefreshDataSourceTableMetadata(
    localVirtualGraph?.dataSourceId,
    tableName,
    setErrorText,
    () => setForceRefresh(true)
  );

  const currentQuery = React.useMemo(() => {
    if (!localVirtualGraph) {
      return '';
    }

    if (typeof localVirtualGraph.from === 'string') {
      return submittedCustomQuery;
    }

    if (!localVirtualGraph.from.value || !localVirtualGraphOptions) {
      return '';
    }
    return generateSqlQueryForTable({
      table: localVirtualGraph.from,
      nativeQuoteCharacter,
      options: localVirtualGraphOptions,
      limit: true,
    });
  }, [
    localVirtualGraph,
    nativeQuoteCharacter,
    localVirtualGraphOptions,
    submittedCustomQuery,
  ]);

  const runningQuery = useProjectDataSourceQuery({
    dataSourceId: localVirtualGraph?.dataSourceId || dataSource?.id,
    existingFields: localVirtualGraph?.fields,
    forceRefresh,
    query: currentQuery,
    virtualGraphOptions: isDataSourceQueryOptionsSupported
      ? localVirtualGraphOptions || undefined
      : undefined,
  });

  const didIsQueryingChange = useDidValueChange(runningQuery.isQuerying);
  React.useEffect(() => {
    if (didIsQueryingChange && !runningQuery.isQuerying && currentQuery) {
      setForceRefresh(false);
      if (runningQuery.errors.length > 0) {
        setErrorText(runningQuery.errors.map((e) => e.message).join('\n'));
      } else if (runningQuery.fields.length > 0) {
        setLocalVirtualGraph((prev) => {
          if (!prev) {
            return prev;
          }
          const mergedFields = runningQuery.fields.map((newField) => {
            const existingField = prev.fields.find(
              (field) => field.value === newField.value
            );
            return existingField
              ? { ...newField, label: existingField.label }
              : newField;
          });
          return {
            ...prev,
            fields: mergedFields,
            previewRows: runningQuery.rows,
            allResultsIncluded: runningQuery.allResultsIncluded,
            dateLastUpdated: Date.now(),
          };
        });
        setErrorText('');
      }
    }
  }, [
    didIsQueryingChange,
    runningQuery,
    currentQuery,
    setLocalVirtualGraph,
    setErrorText,
  ]);

  React.useEffect(() => {
    // Only initialize localVirtualGraph if it's null (first load).
    if (virtualGraph && !localVirtualGraph) {
      setLocalVirtualGraph(virtualGraph);
      setLocalVirtualGraphOptions(virtualGraph.options);
      const extracted = extractAdvancedOptions(virtualGraph.options);
      setAdvancedOptions(
        extracted.length > 0 ? extracted : [{ property: '', value: '' }]
      );
      setIsAdvancedOpen(extracted.length > 0);
      initializeFromVirtualGraph(virtualGraph);
    }
  }, [
    virtualGraph,
    localVirtualGraph,
    setLocalVirtualGraph,
    setLocalVirtualGraphOptions,
    extractAdvancedOptions,
    initializeFromVirtualGraph,
  ]);

  useRefreshVgTables(virtualGraph);

  const isDirty = useIsVgDirty(
    virtualGraph,
    localVirtualGraph,
    localVirtualGraphOptions
  );

  const handleCloseDialog = React.useCallback(() => {
    setLocalVirtualGraph(null);
    setLocalVirtualGraphOptions(null);
    setErrorText('');
    dispatch(
      uiSlice.actions.toggledVirtualGraphPreviewDialog({ isOpen: false })
    );
  }, [
    dispatch,
    setLocalVirtualGraph,
    setLocalVirtualGraphOptions,
    setErrorText,
  ]);

  const handleSave = React.useCallback(() => {
    if (
      errorText ||
      !localVirtualGraph ||
      !localVirtualGraphOptions ||
      !virtualGraph
    ) {
      return;
    }

    const virtualGraphToSave: ProjectVirtualGraph = {
      ...virtualGraph,
      ...omit(localVirtualGraph, 'errors', 'isSettingsOpenKey'),
      options: localVirtualGraphOptions,
    };

    dispatch(
      projectsActionCreators.updatedVirtualGraphForProject(
        projectId,
        virtualGraphToSave
      )
    );

    if (diagram) {
      commitDiagramModel(diagram, (model) => {
        if (localVirtualGraph.name !== virtualGraph.name) {
          updateResourceName(virtualGraph.id, localVirtualGraph.name)(model);
        }

        const resourceKey = getResourceKey(virtualGraph.id);
        const areFieldReferencesKept =
          typeof localVirtualGraph.from === 'object' &&
          typeof virtualGraph.from === 'object' &&
          getDataSourceTableKey(localVirtualGraph.from) ===
            getDataSourceTableKey(virtualGraph.from);

        if (localVirtualGraph.dataSourceType) {
          updateResourceDataSourceType(
            resourceKey,
            localVirtualGraph.dataSourceType
          )(model);
        }
        updateMappingsForResourceFields({
          areFieldReferencesKept,
          newFields: localVirtualGraph.fields,
          oldFields: virtualGraph.fields,
          resourceKey,
        })(model);
      });

      diagram.undoManager.clear();
    }

    handleCloseDialog();
  }, [
    diagram,
    dispatch,
    errorText,
    handleCloseDialog,
    localVirtualGraph,
    localVirtualGraphOptions,
    projectId,
    virtualGraph,
  ]);

  const handleRevert = React.useCallback(() => {
    if (virtualGraph) {
      setLocalVirtualGraph(virtualGraph);
      setLocalVirtualGraphOptions(virtualGraph.options);
      const extracted = extractAdvancedOptions(virtualGraph.options);
      setAdvancedOptions(
        extracted.length > 0 ? extracted : [{ property: '', value: '' }]
      );
    }
  }, [
    virtualGraph,
    setLocalVirtualGraph,
    setLocalVirtualGraphOptions,
    extractAdvancedOptions,
  ]);

  const handleUpdateDataSource = React.useCallback(() => {
    openPanel?.({
      renderPanel: DataSourceTypeSelectionPanel,
      props: {},
    });
    // Note: refreshDataSources will be called in DataSourceTypeSelectionPanel
  }, [openPanel]);

  const handleSetContents = React.useCallback(
    (update: Partial<ProjectVirtualGraph>, isSettingFrom?: boolean) => {
      setLocalVirtualGraph((prev) =>
        !prev ||
        (!isSettingFrom &&
          typeof update.from === 'object' &&
          typeof prev.from === 'object' &&
          getDataSourceTableKey(update.from) !==
            getDataSourceTableKey(prev.from))
          ? prev
          : {
              ...prev,
              ...update,
              dateLastUpdated: Date.now(),
            }
      );
    },
    [setLocalVirtualGraph]
  );

  const handleFieldLabelChange = React.useCallback(
    (value: string, label: string) => {
      setLocalVirtualGraph((prev) => {
        if (!prev) {
          return prev;
        }
        const updatedFields = prev.fields.map((field) =>
          field.value === value ? { ...field, label } : field
        );
        return {
          ...prev,
          fields: updatedFields,
        };
      });
    },
    [setLocalVirtualGraph]
  );

  const handleSelectTable = React.useCallback(
    (tableKey: string) => {
      const table = tables.find((t) => getDataSourceTableKey(t) === tableKey);
      if (table && virtualGraph) {
        handleSetContents(
          {
            from: {
              name: table.name,
              text: table.name,
              value: table.name,
              catalog: table.catalog,
              schema: table.schema,
            },
            // Clear fields/previewRows - the query hook will fetch new data
            fields: [],
            previewRows: [],
            allResultsIncluded: false,
          },
          true
        );
      }
    },
    [tables, virtualGraph, handleSetContents]
  );

  const handleCustomQueryChange = React.useCallback(
    (query: string) => {
      setLocalVirtualGraph((prev) => (prev ? { ...prev, from: query } : prev));
    },
    [setLocalVirtualGraph]
  );

  const handleRunQuery = React.useCallback(() => {
    if (
      localVirtualGraph &&
      typeof localVirtualGraph.from === 'string' &&
      localVirtualGraph.from.trim()
    ) {
      // Set the submitted query to trigger execution
      setSubmittedCustomQuery(localVirtualGraph.from);
      setForceRefresh(true);
    }
  }, [localVirtualGraph, setSubmittedCustomQuery, setForceRefresh]);

  // Set up drawer state
  React.useEffect(() => {
    setFullSize(true);
    setDialogTitle(copy.components.virtualGraphPreviewDialog.title);

    return () => {
      setFullSize(false);
    };
  }, [setFullSize, setDialogTitle]);

  const warningText = React.useMemo(() => {
    if (!virtualGraph) {
      return '';
    }

    const areOptionsChanged = !isEqual(
      virtualGraph.options,
      localVirtualGraphOptions
    );
    const isFromChanged = !isEqual(virtualGraph.from, localVirtualGraph?.from);

    if (areOptionsChanged || isFromChanged) {
      // Only show warning if the resource has any mappings
      const resourceNode = diagram?.findNodeForKey(
        getResourceKey(virtualGraph.id)
      ) as ResourceNode | null;
      const hasMappings =
        resourceNode && getMappingsForResource(resourceNode).length > 0;

      if (hasMappings) {
        return copy.components.virtualGraphPreviewDialog.warningTitle;
      }
    }

    return '';
  }, [diagram, virtualGraph, localVirtualGraphOptions, localVirtualGraph]);

  const hasUnsubmittedQueryChanges = React.useMemo(() => {
    if (!localVirtualGraph || typeof localVirtualGraph.from !== 'string') {
      return false;
    }
    return localVirtualGraph.from !== submittedCustomQuery;
  }, [localVirtualGraph, submittedCustomQuery]);

  if (!virtualGraph || !localVirtualGraph || !localVirtualGraphOptions) {
    return null;
  }

  const asideHeader = (
    <div className={styles.dataSourceHeader}>
      <div className={styles.dataSourceHeaderRow}>
        {dataSourceIcon?.isBlueprintIcon ? (
          <div className={styles.dataSourceIconWrapper}>
            <BlueprintIcon icon={dataSourceIcon.icon} iconSize={24} />
          </div>
        ) : null}
        {dataSourceIcon && !dataSourceIcon.isBlueprintIcon ? (
          <div className={styles.dataSourceIconWrapper}>
            <img
              alt="Data source icon"
              height="24px"
              src={dataSourceIcon.icon}
            />
          </div>
        ) : null}
        <Button onClick={handleUpdateDataSource} size="md" variant="secondary">
          {
            copy.components.projectResourceDialog.projectVirtualGraphPanel.form
              .replace
          }
        </Button>
      </div>
      <Body size="md" weight="semibold">
        {dataSource?.id ||
          copy.components.virtualGraphPreviewDialog.noDataSource}
      </Body>
    </div>
  );

  const asideContent = (
    <>
      <VirtualGraphTableSelection
        customQuery={
          typeof localVirtualGraph.from === 'string'
            ? localVirtualGraph.from
            : ''
        }
        expandAllTables={expandAllTables}
        isMetadataRefreshable={isMetadataRefreshable}
        isQueryRunning={runningQuery.isQuerying}
        isRefreshingMetadata={isRefreshingMetadata}
        isTableTreeOpen={isTableTreeOpen}
        isUsingCustomQuery={typeof localVirtualGraph.from === 'string'}
        onCustomQueryChange={handleCustomQueryChange}
        onModeChange={handleSetIsUsingCustomQuery}
        onRefreshMetadata={refreshDataSourceTypeMetadata}
        onRunQuery={handleRunQuery}
        onSelectTable={handleSelectTable}
        onTableTreeOpenChange={setIsTableTreeOpen}
        selectedTable={
          typeof localVirtualGraph.from === 'object'
            ? getDataSourceTableKey(localVirtualGraph.from)
            : null
        }
        tables={tables}
      />

      <Accordion
        icon="left"
        isOpen={isPropertiesOpen}
        label={
          copy.components.projectResourceDialog.projectVirtualGraphPanel.form
            .resourceProperties
        }
        onInteraction={setIsPropertiesOpen}
        size="md"
      >
        <VirtualGraphOptionsForm
          onSetVirtualGraphOptions={setLocalVirtualGraphOptions}
          virtualGraphOptions={localVirtualGraphOptions}
        />

        <Accordion
          icon="left"
          isOpen={isAdvancedOpen}
          label={copy.components.virtualGraphPreviewDialog.advancedOptions.title(
            advancedOptions.filter((opt) => opt.property.trim() !== '').length
          )}
          onInteraction={setIsAdvancedOpen}
          size="md"
        >
          <AdvancedOptions
            onOptionsChange={updateAdvancedOptions}
            options={advancedOptions}
            propertyMetadata={advancedPropertyMetadata}
            propertySuggestions={propertySuggestions}
          />
        </Accordion>
      </Accordion>
    </>
  );

  const asideFooter = (
    <SidebarFooterButtons
      isDirty={isDirty}
      isSaveDisabled={Boolean(errorText) || hasUnsubmittedQueryChanges}
      onRevert={handleRevert}
      onSave={handleSave}
      revertLabel={copy.components.virtualGraphPreviewDialog.revertButton}
      saveLabel={copy.components.virtualGraphPreviewDialog.saveButton}
      warningText={warningText}
    />
  );

  const mainContent = (
    <VirtualGraphPreview
      error={errorText}
      isLoading={isRefreshing || runningQuery.isQuerying}
      onFieldLabelChange={handleFieldLabelChange}
      onRefresh={refreshPreview}
      onResourceNameChange={(name) => {
        setLocalVirtualGraph((prev) => (prev ? { ...prev, name } : prev));
      }}
      resourceName={localVirtualGraph.name}
      rowCount={localVirtualGraph.previewRows?.length ?? null}
      virtualGraph={localVirtualGraph}
    />
  );

  return (
    <PanelWithSidebar
      asideContent={asideContent}
      asideFooter={asideFooter}
      asideHeader={asideHeader}
      mainContent={mainContent}
    />
  );
};
